export const MISSING_ACCESS_TOKEN = "MISSING_ACCESS_TOKEN";

const REQUEST_CONTEXT_PREFIX = "onegradRecommendationRequest:";
const LAST_CORRELATION_PREFIX = "onegradRecommendationCorrelation:";
const recommendationFlows = new Map();
const requestContexts = new Map();

const createId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createJourneyCorrelationId = () => createId();

const getStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const readStoredContext = (accountId) => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const value = JSON.parse(
      storage.getItem(`${REQUEST_CONTEXT_PREFIX}${accountId}`) || "null"
    );
    return value?.idempotencyKey && value?.correlationId ? value : null;
  } catch {
    return null;
  }
};

const persistContext = (accountId, context) => {
  requestContexts.set(accountId, context);
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(
      `${REQUEST_CONTEXT_PREFIX}${accountId}`,
      JSON.stringify(context)
    );
  } catch {
    // Storage can be unavailable in private browsing. The in-memory context
    // still deduplicates callbacks in this tab and the server remains the
    // cross-tab source of truth.
  }
};

export const getRecommendationRequestContext = (
  accountId,
  preferredCorrelationId
) => {
  const normalizedAccountId = String(accountId || "").trim();
  if (!normalizedAccountId) {
    throw new Error("Recommendation generation requires an account identity");
  }

  const existing =
    requestContexts.get(normalizedAccountId) ||
    readStoredContext(normalizedAccountId);
  if (existing) {
    requestContexts.set(normalizedAccountId, existing);
    return existing;
  }

  const context = {
    correlationId: preferredCorrelationId || createId(),
    idempotencyKey: `${normalizedAccountId}.${createId()}`,
  };
  persistContext(normalizedAccountId, context);
  return context;
};

export const clearRecommendationRequestContext = (accountId) => {
  const normalizedAccountId = String(accountId || "").trim();
  requestContexts.delete(normalizedAccountId);
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(`${REQUEST_CONTEXT_PREFIX}${normalizedAccountId}`);
  } catch {
    // See persistContext: storage failure must not break authentication.
  }
};

export const rememberRecommendationCorrelation = (
  accountId,
  correlationId
) => {
  const storage = getStorage();
  if (!storage || !accountId || !correlationId) return;
  try {
    storage.setItem(
      `${LAST_CORRELATION_PREFIX}${String(accountId).trim()}`,
      correlationId
    );
  } catch {
    // Correlation improves observability but must never block the user flow.
  }
};

export const getLastRecommendationCorrelationId = (accountId) => {
  const storage = getStorage();
  if (!storage || !accountId) return "";
  try {
    return (
      storage.getItem(
        `${LAST_CORRELATION_PREFIX}${String(accountId).trim()}`
      ) || ""
    );
  } catch {
    return "";
  }
};

const getRegisteredUser = (authData, completedCollegeFinder) => ({
  token: authData.accessToken,
  tier: authData.tier,
  completedCollegeFinder,
  name: authData.name,
  picture: authData.picture,
  id: authData.id,
  email: authData.email,
});

const getOrCreateFlow = ({ authData, generateRecommendations }) => {
  const accountId = String(authData.id || "").trim();
  const existing = recommendationFlows.get(accountId);
  if (existing) return existing;

  const flowId = createId();
  const requestContext = getRecommendationRequestContext(
    accountId,
    authData.correlationId
  );
  const provisionalUser = getRegisteredUser(authData, false);
  const flow = {
    accountId,
    flowId,
    requestContext,
    terminalClaimed: false,
    provisionalUser,
  };

  flow.promise = Promise.resolve()
    .then(() =>
      generateRecommendations({
        accessToken: authData.accessToken,
        ...requestContext,
      })
    )
    .then((recommendations) => {
      rememberRecommendationCorrelation(
        accountId,
        requestContext.correlationId
      );
      clearRecommendationRequestContext(accountId);
      return {
        recommendations,
        user: getRegisteredUser(authData, true),
      };
    })
    .finally(() => {
      if (recommendationFlows.get(accountId) === flow) {
        recommendationFlows.delete(accountId);
      }
    });

  recommendationFlows.set(accountId, flow);
  return flow;
};

const claimTerminalState = (flow, isFlowActive) => {
  if (flow.terminalClaimed || !isFlowActive(flow.flowId)) return false;
  flow.terminalClaimed = true;
  return true;
};

export const completeCollegeFinderRegistration = async ({
  authData,
  setUser,
  generateRecommendations,
  recommendationInFlight,
  onFlowStart = () => {},
  isFlowActive = () => true,
}) => {
  if (
    typeof authData?.accessToken !== "string" ||
    !authData.accessToken.trim()
  ) {
    const error = new Error("Registration did not return an access token");
    error.code = MISSING_ACCESS_TOKEN;
    throw error;
  }

  const accountId = String(authData.id || "").trim();
  const duplicate = recommendationFlows.has(accountId);
  const flow = getOrCreateFlow({
    authData,
    generateRecommendations,
  });
  onFlowStart(flow.flowId, flow.requestContext, duplicate);
  if (!duplicate) {
    // The flow is active before global identity changes. Authentication is
    // available for protected calls, but redirect readiness is withheld.
    setUser(flow.provisionalUser);
  }
  if (recommendationInFlight) recommendationInFlight.current = true;

  try {
    const outcome = await flow.promise;
    const terminalOwner = claimTerminalState(flow, isFlowActive);
    if (terminalOwner) setUser(outcome.user);
    return {
      generated: true,
      flowId: flow.flowId,
      terminalOwner,
      duplicate,
      requestContext: flow.requestContext,
    };
  } catch (error) {
    error.flowId = flow.flowId;
    error.requestContext = flow.requestContext;
    error.terminalOwner = claimTerminalState(flow, isFlowActive);
    throw error;
  } finally {
    if (recommendationInFlight) recommendationInFlight.current = false;
  }
};

export const __resetRegistrationFlowsForTests = () => {
  recommendationFlows.clear();
  requestContexts.clear();
};
